import React, { useEffect } from 'react';
import { Col, Form, FormInstance, Row } from 'antd';

import { ActiveFilter } from '../../common/ActiveRadioGroup';
import { FilteredRecruitmentsTable } from '../../common/FilteredRecruitmentsTable';
import { Filter } from '../../common/filters.type';
import { SearchFormFieldEnum } from '../../common/searchFormField.types';
import { CriteriaOperatorEnum, StatusType } from '../../common/types';
import { useGetProject } from '../../common/useGetProject';
import { useGetRecruitmentStatusDictionary } from '../../common/useGetRecruitmentStatusDictionary';
import { FormDoubleInput } from '../../components/forms/FormDoubleInput';
import { FormProjectSelectWithSearch } from '../../components/forms/FormProjectSelectWithSearch';
import { FormSelect } from '../../components/forms/FormSelect';
import { FormTextArea } from '../../components/forms/FormTextArea';
import { ProjectType } from '../projects/ProjectsTable.types';
import { responsibleOptions } from '../recruitment-details/StatusAndNoteForm';
import { useProjectRecruitmentsTableColumnDefinitions } from '../recruitments/useRecruitmentsTableColumnDefinitions';

import { FormCandidateSelectWithSearch } from './FormCandidateSelectWithSearch';

const mapErrorFieldsToFormLabels = {
  candidateUuid: 'Candidate',
  candidate: 'Candidate',
  status: 'Status',
  responsible: 'Responsible',
  project: 'Project',
};

type AddCandidateToRecruitmentFormProps = {
  form: FormInstance;
};

const getAddProjectRecruitmentForm = (projectData: ProjectType) => {
  const AddProjectRecruitmentForm: React.FC<AddCandidateToRecruitmentFormProps> = ({ form }) => {
    const { data: statusesData, isLoading: statusesLoading } = useGetRecruitmentStatusDictionary();
    const statusesOptions =
      !statusesLoading && statusesData
        ? statusesData.map((status) => ({
            value: status.id,
            content: status.name,
          }))
        : [];

    const currentStatusName = Form.useWatch('status', form);
    const currentStatus = statusesData?.find((status) => status.id === currentStatusName);

    return (
      <Form form={form} style={{ flexGrow: '1' }} layout="vertical" colon={false}>
        <Row gutter={[10, 5]}>
          <Col span={12}>
            <FormCandidateSelectWithSearch />

            <FormDoubleInput
              leftInput={
                <FormSelect name="status" label="Status" allowClear={false} options={statusesOptions} required />
              }
              rightInput={
                currentStatus?.actionRequired && (
                  <FormSelect
                    name="responsible"
                    label="Responsible"
                    allowClear={false}
                    options={responsibleOptions(currentStatus, projectData)}
                    required
                  />
                )
              }
            />
          </Col>
          <Col span={12}>
            <FormTextArea label="Note content" name="content" rows={4} />
          </Col>
        </Row>
      </Form>
    );
  };
  return AddProjectRecruitmentForm;
};

const DuplicateRecruitmentsForm: React.FC<AddCandidateToRecruitmentFormProps> = ({ form }) => {
  const currentProjectNameUUID = Form.useWatch('projectUuid', form);

  const { data: projectData } = useGetProject(currentProjectNameUUID);

  const { data: statusesData, isLoading: statusesLoading } = useGetRecruitmentStatusDictionary();
  const statusesOptions =
    !statusesLoading && statusesData
      ? statusesData.map((status) => ({
          value: status.id,
          content: status.name,
        }))
      : [];

  const currentStatusName = Form.useWatch('status', form);
  const currentStatus = statusesData?.find((status) => status.id === currentStatusName);

  useEffect(() => {
    form.setFieldValue('responsible', null);
    form.setFieldValue('status', null);
  }, [projectData, form]);

  useEffect(() => {
    form.setFieldValue('responsible', null);
  }, [currentStatus, form]);

  return (
    <Form form={form} style={{ flexGrow: '1' }} layout="vertical" colon={false}>
      <Row gutter={[10, 5]}>
        <Col span={12}>
          <FormProjectSelectWithSearch name="projectUuid" activeProjects />

          <FormDoubleInput
            leftInput={<FormSelect name="status" label="Status" allowClear={false} options={statusesOptions} />}
            rightInput={
              currentStatus?.actionRequired && (
                <FormSelect
                  name="responsible"
                  label="Responsible"
                  allowClear={false}
                  options={responsibleOptions(currentStatus, projectData)}
                />
              )
            }
          />
        </Col>
        <Col span={12}>
          <FormTextArea label="Note content" name="content" rows={4} />
        </Col>
      </Row>
    </Form>
  );
};

export const ProjectRecruitmentsTable: React.FC<{ projectData: ProjectType }> = ({ projectData }) => {
  const defaultFilter: Filter = {
    id: 0,
    name: '',
    isDefault: false,
    sort: [],
    criteria: [
      {
        field: SearchFormFieldEnum.ProjectUUID,
        value: projectData.uuid,
        operator: CriteriaOperatorEnum.EQ,
      },
    ],
  };

  const mapRecordToPayload = (record: { candidateUuid: string; responsible: string; status: StatusType }) => ({
    data: {
      candidateUuid: record['candidateUuid'],
      projectUuid: projectData.uuid,
      responsible: record.responsible,
      status: record.status,
    },
  });

  const AddProjectRecruitmentForm = getAddProjectRecruitmentForm(projectData);

  return (
    <FilteredRecruitmentsTable
      defaultFilter={defaultFilter}
      defaultActiveFilter={ActiveFilter.ACTIVE}
      itemForm={AddProjectRecruitmentForm}
      duplicateForm={DuplicateRecruitmentsForm}
      getColumnsDefinition={useProjectRecruitmentsTableColumnDefinitions}
      mapRecordToSavePayload={mapRecordToPayload}
      duplicateButtonName="Duplicate to another project"
      withClone={true}
      mapErrorFieldsToFormLabels={mapErrorFieldsToFormLabels}
    />
  );
};
